<template>
  <div class="cms-story-form">
    <cms-form-header
      :title="isEditing ? $t('cms.stories.editStory') : $t('cms.stories.createStory')"
      :disabled="isSaving || isLoadingStory"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    />

    <cms-form-layout>
      <form @submit.prevent="handleSubmit">
        <cms-form-section :title="$t('cms.stories.translationsSection')">
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <div class="cms-form-grid">
                <cms-input
                  v-model="getTranslation(locale).title"
                  :label="`${$t('cms.stories.storyTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  v-model="getTranslation(locale).slug"
                  :label="`${$t('cms.stories.slug')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  v-model="getTranslation(locale).custom_url"
                  :label="`${$t('cms.stories.customUrl')} (${locale.toUpperCase()})`"
                  :help-text="$t('cms.stories.customUrlHelp')"
                  :full-width="true"
                />
                <div class="cms-form-group cms-form-group-full">
                  <cms-editor
                    :ref="(el: any) => setContentEditorRef(locale, el)"
                    v-model="getTranslation(locale).content"
                    :label="`${$t('cms.stories.content')} (${locale.toUpperCase()})`"
                  />
                  <p class="cms-form-help cms-shortcode-help">
                    {{ $t('cms.stories.contentShortcodeHelp') }}
                  </p>
                  <cms-button
                    type="button"
                    variant="secondary"
                    size="sm"
                    :disabled="isSaving || isLoadingStory"
                    @mousedown.prevent="insertNewsletterShortcodeAtCursor(locale)"
                  >
                    {{ $t('cms.stories.insertNewsletterShortcode') }}
                  </cms-button>
                </div>
                <cms-input
                  v-model="getTranslation(locale).excerpt"
                  :label="`${$t('cms.stories.excerpt')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-input
                  v-model="getTranslation(locale).seo_title"
                  :label="`${$t('cms.stories.seoTitle')} (${locale.toUpperCase()})`"
                  :full-width="true"
                />
                <cms-textarea
                  v-model="getTranslation(locale).seo_description"
                  :label="`${$t('cms.stories.seoDescription')} (${locale.toUpperCase()})`"
                  :rows="3"
                  :full-width="true"
                  :max-length="140"
                  :show-counter="true"
                />
                <cms-list-input
                  v-model="getTranslation(locale).seo_keywords"
                  :label="`${$t('cms.stories.seoKeywords')} (${locale.toUpperCase()})`"
                  :placeholder="$t('cms.stories.seoKeywordsPlaceholder')"
                  :add-button-text="$t('cms.activities.addKeyword')"
                  :full-width="true"
                />
              </div>
            </template>
          </cms-translation-tabs>
        </cms-form-section>
        <cms-form-section :title="$t('cms.stories.generalInfo')">
          <div class="cms-form-grid">
            <div class="cms-form-group">
              <label class="cms-input-label">{{ $t('cms.stories.author') }}</label>
              <v-select
                v-model="form.cms_team_member_id"
                :options="authorOptions"
                label="label"
                :reduce="reduceSelectOptionToNumber"
                class="cms-relationship-select"
                :placeholder="$t('cms.stories.authorPlaceholder')"
                :clearable="true"
              />
            </div>

            <div class="cms-form-group">
              <label class="cms-input-label">{{ $t('cms.stories.category') }}</label>
              <div class="cms-category-select-row">
                <v-select
                  v-model="form.cms_story_category_ids"
                  :options="categoryOptions"
                  multiple
                  label="label"
                  :reduce="reduceSelectOptionValue"
                  class="cms-relationship-select"
                  :placeholder="$t('cms.stories.categoryPlaceholder')"
                  :close-on-select="false"
                  :clearable="true"
                />
                <cms-button variant="secondary" size="sm" icon="plus" @click="showStoryCategoryModal = true">
                  {{ $t('cms.storyCategories.addNew') }}
                </cms-button>
              </div>
            </div>

            <cms-list-input
              v-model="form.tags"
              :label="$t('cms.stories.tags')"
              :placeholder="$t('cms.stories.tagsPlaceholder')"
              :add-button-text="$t('cms.activities.addItem')"
              :full-width="true"
            />

            <div class="cms-form-group cms-form-group-full">
              <label>{{ $t('cms.stories.scheduledPublish') }}</label>
              <input
                v-model="form.published_at"
                type="datetime-local"
                class="cms-input"
              />
              <p class="cms-form-help">{{ $t('cms.stories.publishDate') }}</p>
            </div>
          </div>
        </cms-form-section>

        <cms-form-section
          v-if="storyId"
          :title="$t('cms.activities.faq')"
        >
          <div v-if="form?.faq?.length === 0" class="cms-faq-empty-state">
            <v-icon icon="help-circle" size="lg" />
            <p>{{ $t('cms.stories.noFaqsYet') }}</p>
            <p class="cms-faq-empty-hint">{{ $t('cms.stories.addFirstFaqHint') }}</p>
          </div>
          <div v-else class="cms-faq-list">
            <div
              v-for="(faqItem, index) in form.faq"
              :key="faqItem.id || `temp-${index}`"
              class="cms-faq-item-wrapper"
              :class="{ 'cms-faq-item-expanded': isFaqExpanded(index) }"
            >
              <div class="cms-faq-item-header" @click="toggleFaqAccordion(index)">
                <div class="cms-faq-item-number">
                  <v-icon
                    :icon="isFaqExpanded(index) ? 'chevron-down' : 'chevron-right'"
                    size="sm"
                    class="cms-faq-accordion-icon"
                  />
                  <v-icon icon="help-circle" size="sm" />
                  <span>{{ $t('cms.activities.faq') }} {{ index + 1 }}</span>
                  <span v-if="getFaqPreviewQuestion(faqItem)" class="cms-faq-preview">
                    {{ getFaqPreviewQuestion(faqItem).length > 50 ? getFaqPreviewQuestion(faqItem).substring(0, 50) + '...' : getFaqPreviewQuestion(faqItem) }}
                  </span>
                </div>
                <div class="cms-faq-item-actions" @click.stop>
                  <cms-button
                    variant="danger"
                    icon="trash"
                    size="sm"
                    :disabled="isSaving || isLoadingStory || isLoadingFaqs"
                    :title="$t('general.remove')"
                    @click="removeFaqItem(index)"
                  >
                    {{ $t('general.remove') }}
                  </cms-button>
                </div>
              </div>
              <div
                class="cms-faq-item-content"
                :class="{ 'cms-faq-content-expanded': isFaqExpanded(index) }"
                @click.stop
              >
                <cms-translation-tabs :languages="faqLanguages" v-model="faqTabLanguage" @click.stop>
                  <template #default="{ locale }">
                    <div class="cms-faq-question-wrapper">
                      <cms-input
                        v-model="getFaqTranslation(faqItem, locale).question"
                        :label="`${$t('cms.activities.faqQuestion')} (${locale.toUpperCase()})`"
                        :full-width="true"
                        :disabled="isSaving || isLoadingStory || isLoadingFaqs"
                        :placeholder="$t('cms.activities.faqQuestionPlaceholder')"
                      />
                    </div>
                    <div class="cms-faq-answer-wrapper">
                      <cms-editor
                        v-model="getFaqTranslation(faqItem, locale).answer"
                        :label="`${$t('cms.activities.faqAnswer')} (${locale.toUpperCase()})`"
                      />
                    </div>
                  </template>
                </cms-translation-tabs>
              </div>
            </div>
          </div>
          <div class="cms-faq-actions">
            <cms-button
              variant="secondary"
              icon="plus"
              :disabled="isSaving || isLoadingStory || isLoadingFaqs"
              @click="addFaqItem"
            >
              {{ $t('cms.activities.addFaqItem') }}
            </cms-button>
          </div>
        </cms-form-section>

      </form>

      <template #sidebar>
        <cms-sidebar>
          <cms-sidebar-section :title="$t('cms.stories.publishing')">
            <label class="cms-checkbox-item">
              <input v-model="form.is_public" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.stories.isPublic') }}</span>
            </label>
            <label class="cms-checkbox-item">
              <input v-model="form.is_featured" type="checkbox" class="cms-checkbox" style="margin-right: 8px;" />
              <span>{{ $t('cms.stories.isFeatured') }}</span>
            </label>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.stories.mediaSection')">
            <template v-if="storyId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :label="$t('cms.stories.featuredImage')"
                :help-text="$t('cms.stories.featuredImageHelp')"
                :initial-media="featuredUploaderInitialMedia"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia"
                  @click="saveFeaturedMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t('cms.stories.uploadFeatured') }}
                </cms-button>
              </div>

              <div class="cms-sidebar-featured-alt">
                <cms-input
                  v-model="getTranslation(currentLanguage).featured_image_alt"
                  :label="`${$t('cms.stories.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                  :help-text="$t('cms.stories.featuredImageAltHelp')"
                  :full-width="true"
                  :disabled="isSaving || isLoadingStory"
                  :show-tooltip-on-hover="false"
                />
              </div>

              <!-- <cms-gallery-upload
                ref="galleryUploadRef"
                v-model="form.gallery"
                :label="$t('cms.stories.gallery')"
                :help-text="$t('cms.stories.galleryHelp')"
                :initial-media="galleryUploaderInitialMedia"
                :max-items="20"
                @uploaded="handleGalleryUploaded"
                @removed="handleGalleryRemoved"
              /> -->
              <!-- <div class="cms-media-actions">
                <cms-button
                  variant="secondary"
                  size="sm"
                  :disabled="!canSaveGalleryMedia"
                  @click="saveGalleryMedia"
                >
                  <v-icon icon="upload" size="sm" />
                  {{ $t('cms.stories.uploadGallery') }}
                </cms-button>
              </div> -->
            </template>
            <template v-else>
              <p class="cms-form-help cms-media-disabled">
                {{ $t('cms.activities.mediaUploadRequiresCreation') }}
              </p>
            </template>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.stories.relationshipsSection')">
            <label class="cms-input-label">{{ $t('cms.stories.relatedActivities') }}</label>
            <v-select
              v-model="form.related_activities"
              :options="activityOptions"
              multiple
              label="label"
              :reduce="reduceActivityOption"
              class="cms-relationship-select"
              :close-on-select="false"
              :clearable="false"
            />
            <p class="cms-form-help">{{ $t('cms.stories.relatedActivitiesHelp') }}</p>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
        </cms-form-layout>

    <cms-story-category-modal
      :show="showStoryCategoryModal"
      @close="showStoryCategoryModal = false"
      @category-created="handleStoryCategoryCreated"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import type { StoryForm, StoryTranslation, FaqItem } from '@/types/cms';
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsButton,
  CmsEditor,
  CmsSidebar,
  CmsSidebarSection,
  CmsTranslationTabs,
  CmsListInput,
  CmsImageUpload,
} from '@/components/cms';
import CmsStoryCategoryModal from '@/components/cms/CmsStoryCategoryModal.vue';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';
import { runCmsMediaSync } from '@/helpers/cmsMediaSaveFlow';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface SelectOption {
  value: number | string;
  label: string;
}

export default defineComponent({
  name: 'TheCmsStoryForm',
  components: {
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsButton,
    CmsEditor,
    CmsSidebar,
    CmsSidebarSection,
    CmsTranslationTabs,
    CmsListInput,
    CmsImageUpload,
    CmsStoryCategoryModal,
    vSelect,
    VIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const storyId = ref(route.params.id ? Number(route.params.id) : null);
    const isEditing = computed(() => storyId.value !== null);

    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const createEmptyTranslation = (locale: string): StoryTranslation => ({
      locale,
      title: '',
      slug: '',
      custom_url: '',
      content: '',
      excerpt: '',
      seo_title: '',
      seo_description: '',
      seo_keywords: [],
      featured_image_alt: '',
    });

    const form = reactive<StoryForm>({
      author_id: null,
      cms_team_member_id: null,
      cms_story_category_ids: [],
      tags: [],
      published_at: '',
      is_public: false,
      is_featured: false,
      related_activities: [],
      featured_image: '',
      gallery: [],
      translations: locales.value.map((locale) => createEmptyTranslation(locale)),
      faq: [],
    });

    const authors = ref<SelectOption[]>([]);
    const authorOptions = computed(() => authors.value);

    const activities = ref<SelectOption[]>([]);
    const activityOptions = computed(() => activities.value);

    const storyCategories = ref<Array<{ id: number; name: string; parent_id: number | null; type: string; depth?: number }>>([]);
    const showStoryCategoryModal = ref(false);

    const categoryOptions = computed(() => {
      const options: SelectOption[] = [];
      const addOptions = (items: typeof storyCategories.value, parentId: number | null, depth: number) => {
        for (const cat of items) {
          if (cat.parent_id == parentId) {
            const indent = '—'.repeat(depth);
            options.push({ value: cat.id, label: indent ? `${indent} ${cat.name}` : cat.name });
            addOptions(items, cat.id, depth + 1);
          }
        }
      };
      addOptions(storyCategories.value, null, 0);
      return options;
    });

    const loadStoryCategories = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.storyCategoryService.listCmsStoryCategories(agencySlug.value, {
          filters: { as_tree: false },
        });
        const list = extractListData(response);
        const items = Array.isArray(list) ? list : [list].filter(Boolean);
        storyCategories.value = items.map((cat: any) => {
          const n = normalizeResource(cat) || cat;
          const tr = Array.isArray(n.translations) ? n.translations[0] : n.translations || {};
          const rawParentId = n.parent_id ?? n.parentId ?? n.attributes?.parent_id ?? null;
          return {
            id: n.id ?? n.attributes?.id,
            name: tr.name ?? n.name ?? '',
            parent_id: rawParentId != null ? Number(rawParentId) : null,
            type: n.type ?? n.attributes?.type ?? 'destination',
          };
        });
      } catch (error) {
        console.error('Failed to load story categories', error);
        storyCategories.value = [];
      }
    };

    const handleStoryCategoryCreated = () => {
      loadStoryCategories();
    };

    const getTranslation = (locale: string): StoryTranslation => {
      let translation = form.translations.find((entry) => entry.locale === locale);
      if (!translation) {
        translation = createEmptyTranslation(locale);
        form.translations.push(translation);
      }
      return translation;
    };

    const contentEditorRefs = ref<Record<string, { insertAtCursor?: (text: string) => boolean }>>({});

    const setContentEditorRef = (locale: string, el: any) => {
      if (el) {
        contentEditorRefs.value[locale] = el;
      }
    };

    const insertNewsletterShortcodeAtCursor = (locale: string) => {
      const editorRef = contentEditorRefs.value[locale];
      if (editorRef?.insertAtCursor?.('[waw_newsletter]')) {
        return;
      }
      // Fallback: append at end if editor ref not ready
      const translation = getTranslation(locale);
      const current = translation.content || '';
      translation.content = current + (current.trim() ? ' ' : '') + '[waw_newsletter]';
    };

    const stripHtmlContent = (value: string | null | undefined) => {
      if (!value) {
        return '';
      }
      return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const requiredTranslationFields: Array<keyof StoryTranslation> = ['title', 'slug', 'content'];

    const translationsAreComplete = computed(() => {
      return form.translations.some((translation) =>
        requiredTranslationFields.every((field) => {
          const value = translation[field];
          if (typeof value === 'string') {
            const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
            return normalized.length > 0;
          }
          return false;
        }),
      );
    });

    const validateTranslations = () => {
      if (translationsAreComplete.value) {
        return true;
      }
      const missingFields = requiredTranslationFields
        .map((field) => t(`cms.stories.${field === 'title' ? 'storyTitle' : field}`))
        .join(', ');
      toast.error(
        t('cms.stories.translationValidationError', {
          fields: missingFields,
          language: t('cms.stories.translationGenericLanguage'),
        }),
      );
      return false;
    };

    const isSaving = ref(false);
    const isLoadingStory = ref(false);

    const extractListData = (response: any) => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const extractStoryData = (response: any) => {
      if (!response) return null;
      const candidates = [response?.data?.data, response?.data, response];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.attributes && typeof candidate.attributes === 'object') {
          return {
            id: candidate.id ?? candidate.attributes.id ?? null,
            ...candidate.attributes,
            relationships: candidate.relationships,
          };
        }
        if (candidate.id || candidate.translations || candidate.media) {
          return candidate;
        }
      }
      return null;
    };

    const toInputDateTime = (value?: string | null) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const tzOffset = date.getTimezoneOffset();
      const local = new Date(date.getTime() - tzOffset * 60000);
      return local.toISOString().slice(0, 16);
    };

    const fromInputDateTime = (value?: string | null) => {
      if (!value) return null;
      return `${value.replace('T', ' ')}:00`;
    };

    const mapStoryToForm = (story: any) => {
      form.cms_team_member_id =
        story.cmsTeamMemberId ??
        story.cms_team_member_id ??
        null;
      form.author_id = story.authorId || null;
      const categories = extractRelationship(story, 'categories');
      const normalizedCategories = normalizeResource(categories);
      if (Array.isArray(normalizedCategories) && normalizedCategories.length) {
        form.cms_story_category_ids = normalizedCategories
          .map((c: any) => c?.id ?? c?.attributes?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else if (story.cmsStoryCategoryIds && Array.isArray(story.cmsStoryCategoryIds)) {
        form.cms_story_category_ids = story.cmsStoryCategoryIds.filter((id: any): id is number => typeof id === 'number');
      } else if (story.categoryId != null || story.cmsStoryCategoryId != null) {
        form.cms_story_category_ids = [story.cmsStoryCategoryId ?? story.categoryId].filter((id: any): id is number => typeof id === 'number');
      } else {
        form.cms_story_category_ids = [];
      }
      form.is_public = !!story.isPublic;
      form.is_featured = !!story.isFeatured;
      form.published_at = toInputDateTime(story.publishedAt);
      if (Array.isArray(story.tags)) {
        form.tags = story.tags;
      } else if (typeof story.tags === 'string') {
        form.tags = story.tags.split(',').map((tag: string) => tag.trim());
      } else {
        form.tags = [];
      }

      const relatedActivities = extractRelationship(story, 'relatedActivities');
      const normalized = normalizeResource(relatedActivities);
      if (Array.isArray(normalized)) {
        form.related_activities = normalized
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else if (Array.isArray(story.relatedActivities)) {
        form.related_activities = story.relatedActivities
          .map((activity: any) => activity?.id)
          .filter((id: any): id is number => typeof id === 'number');
      } else {
        form.related_activities = [];
      }
    };

    const applyTranslationsFromStory = (story: any, locale: string) => {
      const normalizedLocale = (locale || '').toLowerCase();
      const translations = extractRelationship(story, 'translations');
      const normalized = normalizeResource(translations);
      const list = Array.isArray(normalized)
        ? normalized
        : Array.isArray(story.translations)
          ? story.translations
          : normalized
            ? [normalized]
            : [];
      const translation = list.find(
        (entry: any) => (entry?.locale || '').toLowerCase() === normalizedLocale,
      ) || null;
      if (!translation) {
        return;
      }
      const target = getTranslation(locale);
      target.title = translation.title || '';
      target.slug = translation.slug || '';
      target.custom_url = translation.custom_url ?? translation.customUrl ?? '';
      target.content = translation.content || '';
      target.excerpt = translation.excerpt || '';
      target.seo_title = translation.seo_title ?? translation.seoTitle ?? '';
      target.seo_description = translation.seo_description ?? translation.seoDescription ?? '';
      const seoKeywords = translation.seo_keywords ?? translation.seoKeywords;
      target.seo_keywords = Array.isArray(seoKeywords)
        ? seoKeywords
        : seoKeywords
          ? [seoKeywords]
          : [];
      target.featured_image_alt = translation.featured_image_alt ?? translation.featuredImageAlt ?? '';
    };

    const buildPayload = () => {
      const translationsPayload = form.translations
        .map((translation) => ({
          locale: translation.locale,
          title: translation.title?.trim(),
          slug: translation.slug?.trim(),
          custom_url: translation.custom_url?.trim() || null,
          content: translation.content,
          excerpt: translation.excerpt,
          seo_title: translation.seo_title,
          seo_description: translation.seo_description,
          seo_keywords: translation.seo_keywords.filter((keyword) => !!keyword?.trim()),
          featured_image_alt: translation.featured_image_alt?.trim() || '',
        }))
        .filter((translation) =>
          requiredTranslationFields.every((field) => {
            const value = translation[field];
            if (typeof value === 'string') {
              const normalized = field === 'content' ? stripHtmlContent(value) : value.trim();
              return normalized.length > 0;
            }
            return false;
          }),
        );
        
      return {
        cms_team_member_id: form.cms_team_member_id,
        cms_story_category_ids: form.cms_story_category_ids?.length ? form.cms_story_category_ids : [],
        tags: form.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0),
        published_at: fromInputDateTime(form.published_at),
        is_public: form.is_public,
        is_featured: form.is_featured,
        related_activities: form.related_activities,
        translations: translationsPayload,
      };
    };

    const extractStoryId = (response: any): number | null => {
      if (!response) return null;
      if (typeof response.id === 'number') {
        return response.id;
      }
      if (typeof response?.data?.id === 'number') {
        return response.data.id;
      }
      if (typeof response?.data?.data?.id === 'number') {
        return response.data.data.id;
      }
      return null;
    };

    const loadAuthors = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.teamMemberService.listCmsTeamMembers(agencySlug.value, {
          perPage: 200,
          order: { key: 'order', direction: 'asc' },
        });
        const list = extractListData(response);
        authors.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractRelationship(normalized, 'translations');
          const normalizedTranslations = normalizeResource(translations);
          const translationList = Array.isArray(normalizedTranslations)
            ? normalizedTranslations
            : normalizedTranslations
              ? [normalizedTranslations]
              : Array.isArray(normalized?.translations)
                ? normalized.translations
                : [];
          const translation =
            translationList.find(
              (entry: any) => (entry?.locale || '').toLowerCase() === (currentLanguage.value || 'en'),
            ) || translationList[0] || {};
          const name = translation?.name || translation?.attributes?.name || `#${normalized.id}`;
          const role = translation?.role || translation?.attributes?.role || '';
          return {
            value: Number(normalized.id),
            label: role ? `${name} (${role})` : name,
          };
        });
      } catch (error) {
        console.error('Failed to load team member authors', error);
      }
    };

    const loadActivities = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        activities.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          return {
            value: normalized.id,
            label: normalized.title || normalized.name || `#${normalized.id}`,
          };
        });
      } catch (error) {
        console.error('Failed to load activities', error);
      }
    };

    const featuredImageUploadRef = ref<any>(null);
    const galleryUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const galleryUploaderInitialMedia = ref<any[]>([]);
    const featuredMediaPayload = ref<any[] | null>(null);
    const galleryMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const pendingGalleryDeletionIds = ref<number[]>([]);
    const isSyncingFeaturedMedia = ref(false);
    const isSyncingGalleryMedia = ref(false);

    const CMS_MEDIA_MODEL_TYPE = 'cms_story';
    const FEATURED_MEDIA_COLLECTION = 'cms_story_featured';
    const GALLERY_MEDIA_COLLECTION = 'cms_story_gallery';
    const MEDIA_DRIVER = 's3';

    const cloneMediaItems = (items: any[] = []) => items.map((item) => JSON.parse(JSON.stringify(item)));

    const getCollectionName = (item: any) => item?.collection_name || item?.collectionName || '';
    const getCustomProperties = (item: any) => item?.custom_properties || item?.customProperties || {};
    const getOrderColumn = (item: any) => item?.order_column ?? item?.orderColumn;

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.preview_url ||
        attributes.preview_url ||
        ''
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return (
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        fallback
      );
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) return null;
      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        storage_url: storageUrl,
        preview_url: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          preview_url: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractMediaItems = (story: any) => {
      const mediaRelationship = extractRelationship(story, 'media');
      const normalizedRelationship = normalizeResource(mediaRelationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(story.media)) {
        return story.media;
      }
      return [];
    };

    const mapStoryMediaCollections = (story: any) => {
      const mediaItems = extractMediaItems(story)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && (item.uuid || item.id));

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === FEATURED_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      const galleryItems = mediaItems
        .filter((item: any) => getCollectionName(item) === GALLERY_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean)
        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      galleryUploaderInitialMedia.value = cloneMediaItems(galleryItems);
      pendingFeaturedDeletion.value = null;
      pendingGalleryDeletionIds.value = [];

      const featuredUrl = getMediaUrl(featuredItems[0]);
      form.featured_image = featuredUrl || '';
      form.gallery = galleryItems.map((item: any) => getMediaUrl(item)).filter((url: string) => !!url);
    };

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set((ids || []).filter((id): id is number => typeof id === 'number' && id > 0)),
      );
      if (!uniqueIds.length) {
        return;
      }
      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const refreshStoryMediaState = async () => {
      if (!agencySlug.value || !storyId.value) {
        return;
      }
      try {
        const response = await api.storyService.getCmsStory(agencySlug.value, storyId.value);
        const storyData = extractStoryData(response);
        if (storyData) {
          mapStoryMediaCollections(storyData);
        }
      } catch (error) {
        console.error('Error refreshing story media', error);
      }
    };

    const confirmFeaturedMedia = async () => {
      if (!storyId.value || isSyncingFeaturedMedia.value || isLoadingStory.value) {
        return;
      }
      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;
      if (!hasUploads && !hasPendingDeletion) {
        return;
      }
      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }
          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: storyId.value,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }
          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshStoryMediaState();
        });
        toast.success(t('cms.activities.featuredMediaSaveSuccess'));
      } catch (error) {
        console.error('Error saving featured media', error);
        toast.error(t('general.errorMessage'));
      }
    };

    const confirmGalleryMedia = async () => {
      if (!storyId.value || isSyncingGalleryMedia.value || isLoadingStory.value) {
        return;
      }
      const payload = galleryMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, GALLERY_MEDIA_COLLECTION);
      const hasDeletions = pendingGalleryDeletionIds.value.length > 0;
      if (!hasUploads && !hasDeletions) {
        return;
      }
      isSyncingGalleryMedia.value = true;
      try {
        if (hasDeletions) {
          await deleteMediaItemsByIds(pendingGalleryDeletionIds.value);
          pendingGalleryDeletionIds.value = [];
        }
        if (hasUploads) {
          await persistCmsMediaAttachment({
            api,
            driver: MEDIA_DRIVER,
            agencySlug: agencySlug.value!,
            mediaItems: payload,
            modelType: CMS_MEDIA_MODEL_TYPE,
            modelId: storyId.value,
            collection: GALLERY_MEDIA_COLLECTION,
          });
        }
        galleryMediaPayload.value = null;
        galleryUploadRef.value?.clearMedia?.();
        toast.success(t('cms.activities.galleryMediaSaveSuccess'));
        await refreshStoryMediaState();
      } catch (error) {
        console.error('Error saving gallery media', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isSyncingGalleryMedia.value = false;
      }
    };

    const persistPendingMediaChanges = async () => {
      await Promise.all([confirmFeaturedMedia(), confirmGalleryMedia()]);
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const handleGalleryUploaded = (media: any[] = []) => {
      galleryMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleGalleryRemoved = (media: any[] = []) => {
      const normalized = Array.isArray(media) ? [...media] : [];
      galleryMediaPayload.value = normalized;
      if (galleryUploaderInitialMedia.value.length === 0) {
        return;
      }
      const currentIds = new Set(
        normalized
          .map((item: any) => (typeof item?.id === 'number' ? item.id : null))
          .filter((id): id is number => typeof id === 'number'),
      );
      const removedItems = galleryUploaderInitialMedia.value.filter(
        (item: any) => typeof item?.id === 'number' && !currentIds.has(item.id),
      );
      if (removedItems.length > 0) {
        const ids = removedItems.map((item: any) => item.id).filter((id: any): id is number => typeof id === 'number');
        pendingGalleryDeletionIds.value = Array.from(new Set([...pendingGalleryDeletionIds.value, ...ids]));
        galleryUploaderInitialMedia.value = galleryUploaderInitialMedia.value.filter((item: any) => !ids.includes(item.id));
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!storyId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isSaving.value &&
        !isLoadingStory.value,
    );

    const canSaveGalleryMedia = computed(
      () =>
        !!storyId.value &&
        galleryMediaPayload.value !== null &&
        !isSyncingGalleryMedia.value &&
        !isSaving.value &&
        !isLoadingStory.value,
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const saveGalleryMedia = async () => {
      await confirmGalleryMedia();
    };

    const faqLanguages = languages;
    const faqTabLanguage = ref(resolveStoredLocale('en'));
    const isLoadingFaqs = ref(false);
    const faqIdsToDelete = ref<number[]>([]);
    const expandedFaqs = ref<Record<number, boolean>>({});

    const getFaqTranslation = (faqItem: FaqItem, locale: string) => {
      const list = faqItem.translations || [];
      let t = list.find((x) => (x.locale || '').toLowerCase() === locale);
      if (!t) {
        t = { locale, question: '', answer: '' };
        list.push(t);
      }
      return t;
    };

    const getFaqPreviewQuestion = (faqItem: FaqItem) => {
      const locale = faqTabLanguage.value || 'en';
      const t = getFaqTranslation(faqItem, locale);
      if (t?.question) return t.question;
      const first = (faqItem.translations || []).find((x: any) => x.question);
      return first ? first.question : '';
    };

    const loadFaqs = async () => {
      if (!storyId.value || !agencySlug.value) {
        form.faq = [];
        expandedFaqs.value = {};
        return;
      }
      isLoadingFaqs.value = true;
      try {
        const response = await api.faqService.listCmsFaqs(agencySlug.value, {
          filters: { cms_story_id: storyId.value },
          perPage: 1000,
        });
        const faqsData = extractListData(response);
        form.faq = faqsData
          .map((faq: any) => {
            const normalized = normalizeResource(faq) || faq;
            const apiTranslations = normalizeResource(extractRelationship(faq, 'translations')) || [];
            const translations = locales.value.map((locale) => {
              const existing = apiTranslations.find(
                (t: any) => (t.locale || '').toLowerCase() === locale,
              );
              return {
                locale,
                question: existing?.question ?? '',
                answer: existing?.answer ?? '',
              };
            });
            return {
              id: normalized.id,
              order: normalized.order ?? 0,
              is_public: true,
              translations,
            };
          })
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
        expandedFaqs.value = {};
      } catch (error) {
        console.error('Error loading story FAQs:', error);
        form.faq = [];
        expandedFaqs.value = {};
      } finally {
        isLoadingFaqs.value = false;
      }
    };

    const saveFaqs = async () => {
      if (!storyId.value || !agencySlug.value) return;
      try {
        for (const faqId of faqIdsToDelete.value) {
          try {
            await api.faqService.deleteCmsFaq(agencySlug.value, faqId);
          } catch (error) {
            console.error(`Error deleting FAQ ${faqId}:`, error);
          }
        }
        faqIdsToDelete.value = [];
        for (let index = 0; index < (form.faq?.length ?? 0); index++) {
          const faqItem = form.faq![index];
          const translations = (faqItem.translations || [])
            .filter((t: any) => (t.question || '').trim() && (t.answer || '').trim())
            .map((t: any) => ({
              locale: t.locale,
              question: (t.question || '').trim(),
              answer: (t.answer || '').trim(),
            }));
          if (translations.length === 0) continue;
          const faqData = {
            cms_story_id: storyId.value,
            order: index,
            is_public: true,
            translations,
          };
          if (faqItem.id) {
            try {
              await api.faqService.updateCmsFaq(agencySlug.value, faqItem.id, faqData);
            } catch (error) {
              console.error(`Error updating FAQ ${faqItem.id}:`, error);
            }
          } else {
            try {
              await api.faqService.createCmsFaq(agencySlug.value, faqData);
            } catch (error) {
              console.error('Error creating story FAQ:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error saving story FAQs:', error);
        throw error;
      }
    };

    const addFaqItem = () => {
      if (!form.faq) form.faq = [];
      const newIndex = form.faq.length;
      form.faq.push({
        id: undefined,
        order: newIndex,
        is_public: true,
        translations: locales.value.map((locale) => ({
          locale,
          question: '',
          answer: '',
        })),
      });
      expandedFaqs.value[newIndex] = true;
    };

    const isFaqExpanded = (index: number) => !!expandedFaqs.value[index];

    const toggleFaqAccordion = (index: number) => {
      expandedFaqs.value = {
        ...expandedFaqs.value,
        [index]: !expandedFaqs.value[index],
      };
    };

    const removeFaqItem = (index: number) => {
      if (!form.faq) return;
      const faqItem = form.faq[index];
      if (faqItem.id) faqIdsToDelete.value.push(faqItem.id);
      form.faq.splice(index, 1);
      const newExpandedFaqs: Record<number, boolean> = {};
      form.faq.forEach((_, newIndex) => {
        if (newIndex < index && expandedFaqs.value[newIndex]) newExpandedFaqs[newIndex] = true;
        else if (newIndex >= index && expandedFaqs.value[newIndex + 1])
          newExpandedFaqs[newIndex] = true;
      });
      expandedFaqs.value = newExpandedFaqs;
    };

    const loadStory = async () => {
      if (!agencySlug.value || !storyId.value) {
        return;
      }
      isLoadingStory.value = true;
      try {
        for (const locale of locales.value) {
          const response = await api.storyService.getCmsStory(agencySlug.value, storyId.value, {
            headers: {
              'Accept-Language': locale,
            },
          });
          const storyData = extractStoryData(response);
          if (!storyData) {
            continue;
          }
          if (locale === locales.value[0]) {
            mapStoryToForm(storyData);
            mapStoryMediaCollections(storyData);
          }
          applyTranslationsFromStory(storyData, locale);
        }
        await loadFaqs();
      } catch (error) {
        console.error('Failed to load story', error);
        toast.error(t('general.errorMessage'));
      } finally {
        isLoadingStory.value = false;
      }
    };

    const persistStory = async (publish = false) => {
      if (!agencySlug.value) return;
      if (!validateTranslations()) {
        return;
      }
      const payload = buildPayload();
      isSaving.value = true;
      try {
        if (storyId.value) {
          await api.storyService.updateCmsStory(agencySlug.value, storyId.value, payload);
        } else {
          const response = await api.storyService.createCmsStory(agencySlug.value, payload);
          const newId = extractStoryId(response);
          if (newId) {
            storyId.value = newId;
            try {
              await router.replace({ name: 'cms.stories.edit', params: { id: newId } });
            } catch (error) {
              console.warn('Router replace failed', error);
            }
            await loadStory();
          }
        }
        await persistPendingMediaChanges();
        if (storyId.value) {
          await saveFaqs();
        }
        if (publish && storyId.value) {
          await api.storyService.publishCmsStory(agencySlug.value, storyId.value, {
            is_public: true,
            published_at: payload.published_at || new Date().toISOString(),
          });
          form.is_public = true;
        }
        toast.success(publish ? t('cms.stories.publishSuccess') : t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving story', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    const reduceActivityOption = (option: SelectOption) => option.value;
    const reduceSelectOptionValue = (option: SelectOption) => option.value;
    const reduceSelectOptionToNumber = (option: SelectOption) => Number(option.value);

    const saveDraft = () => {
      form.is_public = false;
      persistStory(false);
    };

    const saveAndPublish = () => {
      form.is_public = true;
      persistStory(true);
    };

    const handleSubmit = () => {
      saveDraft();
    };

    const goBack = () => {
      router.push({ name: 'cms.stories' });
    };

    watch(
      () => route.params.id,
      (newId) => {
        storyId.value = newId ? Number(newId) : null;
        if (storyId.value) {
          loadStory();
        } else {
          form.cms_team_member_id = null;
          form.author_id = null;
          form.cms_story_category_ids = [];
          form.tags = [];
          form.published_at = '';
          form.is_public = false;
          form.is_featured = false;
          form.related_activities = [];
          form.featured_image = '';
          form.gallery = [];
          form.translations = locales.value.map((locale) => createEmptyTranslation(locale));
          form.faq = [];
          expandedFaqs.value = {};
          featuredUploaderInitialMedia.value = [];
          galleryUploaderInitialMedia.value = [];
        }
      },
      { immediate: true },
    );

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadAuthors();
          loadActivities();
          loadStoryCategories();
          if (storyId.value) {
            loadStory();
          }
        }
      },
      { immediate: true },
    );

    return {
      t,
      form,
      isEditing,
      isSaving,
      isLoadingStory,
      languages,
      currentLanguage,
      getTranslation,
      setContentEditorRef,
      insertNewsletterShortcodeAtCursor,
      authorOptions,
      categoryOptions,
      activityOptions,
      reduceActivityOption,
      reduceSelectOptionValue,
      reduceSelectOptionToNumber,
      featuredImageUploadRef,
      galleryUploadRef,
      featuredUploaderInitialMedia,
      galleryUploaderInitialMedia,
      canSaveFeaturedMedia,
      canSaveGalleryMedia,
      saveFeaturedMedia,
      saveGalleryMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      handleGalleryUploaded,
      handleGalleryRemoved,
      saveDraft,
      saveAndPublish,
      handleSubmit,
      goBack,
      storyId,
      showStoryCategoryModal,
      handleStoryCategoryCreated,
      expandedFaqs,
      faqLanguages,
      faqTabLanguage,
      isLoadingFaqs,
      getFaqTranslation,
      getFaqPreviewQuestion,
      isFaqExpanded,
      addFaqItem,
      removeFaqItem,
      toggleFaqAccordion,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-story-form {
  color: $cms-text-primary;
}

.cms-category-select-row {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;

  .cms-form-group {
    flex: 1;
  }
}

.cms-story-actions {
  display: flex;
  justify-content: flex-end;
  gap: $cms-spacing-sm;
  margin-top: $cms-spacing-lg;
}


.cms-shortcode-help {
  margin-top: $cms-spacing-sm;

  + .cms-button {
    margin-top: $cms-spacing-xs;
  }
}

.cms-media-actions {
  margin-top: $cms-spacing-xs;
  display: flex;
  justify-content: flex-end;
}

.cms-media-disabled {
  color: $cms-text-secondary;
}


.cms-relationship-select {
  width: 100%;
}

// FAQ section (match activity form)
.cms-faq-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: $cms-background-gray-medium;
  border-radius: $cms-radius-lg;
  border: 2px dashed $cms-border-color;
  color: $cms-text-secondary;

  .v-icon {
    color: $cms-text-muted;
    margin-bottom: $cms-spacing-md;
  }

  p {
    margin: 0.5rem 0;
    font-size: $cms-font-size-base;
    color: $cms-text-secondary;
  }

  .cms-faq-empty-hint {
    font-size: $cms-font-size-sm;
    color: $cms-text-muted;
  }
}

.cms-faq-list {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-lg;
  margin-bottom: $cms-spacing-lg;
}

.cms-faq-item-wrapper {
  background: white;
  border-radius: $cms-radius-lg;
  border: 1px solid $cms-border-color;
  overflow: hidden;
  transition: all $cms-transition-base;
  box-shadow: $cms-shadow-sm;

  &:hover {
    box-shadow: $cms-shadow-md;
    border-color: $cms-primary-color;
  }

  .cms-faq-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $cms-spacing-md $cms-spacing-lg;
    background: linear-gradient(135deg, $cms-background-gray-light 0%, $cms-background-gray-medium 100%);
    border-bottom: 1px solid $cms-border-color;
    cursor: pointer;
    user-select: none;
    transition: background-color $cms-transition-fast;

    &:hover {
      background: linear-gradient(135deg, $cms-background-gray-medium 0%, $cms-background-gray-light 100%);
    }

    .cms-faq-item-number {
      display: flex;
      align-items: center;
      gap: $cms-spacing-sm;
      font-weight: 600;
      font-size: $cms-font-size-base;
      color: $cms-text-primary;
      flex: 1;
      min-width: 0;

      .cms-faq-accordion-icon {
        color: $cms-text-secondary;
        transition: transform $cms-transition-base;
        flex-shrink: 0;
      }

      .v-icon:not(.cms-faq-accordion-icon) {
        color: $cms-primary-color;
        flex-shrink: 0;
      }

      span:not(.cms-faq-preview) {
        user-select: none;
        flex-shrink: 0;
      }

      .cms-faq-preview {
        margin-left: $cms-spacing-sm;
        font-weight: 400;
        font-size: $cms-font-size-sm;
        color: $cms-text-secondary;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
      }
    }

    .cms-faq-item-actions {
      display: flex;
      align-items: center;
      gap: $cms-spacing-xs;
      flex-shrink: 0;
    }

    .cms-btn {
      min-width: auto;
      padding: $cms-spacing-xs $cms-spacing-sm;
      height: auto;
    }
  }

  &.cms-faq-item-expanded {
    .cms-faq-accordion-icon {
      transform: rotate(90deg);
    }
  }

  .cms-faq-item-content {
    max-height: 0;
    overflow: hidden;
    transition: max-height $cms-transition-base, padding $cms-transition-base;
    padding: 0 $cms-spacing-lg;
    background: white;

    &.cms-faq-content-expanded {
      max-height: 5000px;
      padding: $cms-spacing-lg;
    }

    .cms-faq-question-wrapper {
      margin-bottom: $cms-spacing-lg;
    }
  }
}

.cms-faq-actions {
  display: flex;
  justify-content: flex-start;
  padding-top: $cms-spacing-md;
  border-top: 1px solid $cms-border-color;
  margin-top: $cms-spacing-md;
}
</style>
